import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { updatePassword } from 'aws-amplify/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { apiClient } from '@/lib/api-client';
import { useAuth } from '@/contexts/AuthContext';
import { useOrganization } from '@/hooks/useOrganization';
import { AppLayout } from '@/components/AppLayout';

interface Organization {
  orgId: string;
  name: string;
  companyName?: string;
  logoUrl?: string;
  timezone?: string;
  language?: string;
  dateFormat?: string;
  subscriptionStatus: string;
}

interface Subscription {
  plan: string;
  status: string;
  jobCount: number;
  candidateCount: number;
  userCount: number;
  maxJobs: number;
  maxCandidatesPerMonth: number;
  maxUsers: number;
}

type PlanType = 'free' | 'starter' | 'pro' | 'enterprise';

interface PlanInfo {
  name: string;
  price: string;
  priceDetail: string;
  features: string[];
  limits: { jobs: string; candidates: string; users: string };
  cta: string;
  highlighted?: boolean;
}

const PLANS: Record<PlanType, PlanInfo> = {
  free: {
    name: 'Free',
    price: 'R$ 0',
    priceDetail: 'para sempre',
    limits: { jobs: '3 vagas', candidates: '30/mês', users: '1 usuário' },
    features: ['Chat IA básico', 'Análise de currículo básica', 'Pipeline Kanban'],
    cta: 'Plano atual',
  },
  starter: {
    name: 'Starter',
    price: 'R$ 99,99',
    priceDetail: '/mês',
    limits: { jobs: '10 vagas', candidates: '200/mês', users: '3 usuários' },
    features: ['Chat IA (20 sessões/mês)', 'Análise completa de currículo', 'Upload em lote (10)', 'Relatórios básicos', 'Suporte por email'],
    cta: 'Assinar Starter',
  },
  pro: {
    name: 'Pro',
    price: 'R$ 299,99',
    priceDetail: '/mês',
    limits: { jobs: '50 vagas', candidates: '1.000/mês', users: '10 usuários' },
    features: ['Chat IA ilimitado', 'Análise + ranking de candidatos', 'Upload em lote (50)', 'Relatórios completos', 'Integração LinkedIn', 'Suporte prioritário'],
    cta: 'Assinar Pro',
    highlighted: true,
  },
  enterprise: {
    name: 'Enterprise',
    price: 'Sob consulta',
    priceDetail: '',
    limits: { jobs: 'Ilimitadas', candidates: 'Ilimitados', users: 'Ilimitados' },
    features: ['Tudo do Pro', 'Limites personalizados', 'API dedicada', 'Suporte dedicado + SLA', 'Onboarding personalizado'],
    cta: 'Fale conosco',
  },
};

const PLAN_ORDER: PlanType[] = ['free', 'starter', 'pro', 'enterprise'];

export default function OrganizationSettingsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, logout } = useAuth();
  const { refresh: refreshOrg } = useOrganization();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [upgradingPlan, setUpgradingPlan] = useState<string | null>(null);
  const [isManaging, setIsManaging] = useState(false);
  const [companyName, setCompanyName] = useState('');
  const [timezone, setTimezone] = useState('America/Sao_Paulo');
  const [language, setLanguage] = useState('pt-BR');
  const [dateFormat, setDateFormat] = useState('DD/MM/YYYY');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  const isOwner = user?.role === 'owner';

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (searchParams.get('success') === 'true') {
      toast.success('Upgrade realizado com sucesso! Seu plano está ativo.');
      fetchData();
      setSearchParams({}, { replace: true });
    } else if (searchParams.get('canceled') === 'true') {
      toast.info('Pagamento cancelado. Você pode tentar novamente quando quiser.');
      setSearchParams({}, { replace: true });
    }
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [orgData, subData] = await Promise.all([
        apiClient.get<Organization>('/organizations/me'),
        apiClient.get<Subscription>('/subscriptions/me'),
      ]);
      setSubscription(subData);
      setCompanyName(orgData.companyName || orgData.name || '');
      setTimezone(orgData.timezone || 'America/Sao_Paulo');
      setLanguage(orgData.language || 'pt-BR');
      setDateFormat(orgData.dateFormat || 'DD/MM/YYYY');
      setLogoPreview(orgData.logoUrl || null);
    } catch (error: any) {
      console.error('Failed to fetch data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpgrade = async (plan: PlanType) => {
    if (plan === 'enterprise') {
      window.open('mailto:contato@talentumiq.ai?subject=Enterprise%20-%20TalentumIQ', '_blank');
      return;
    }
    setUpgradingPlan(plan);
    try {
      const data = await apiClient.post<{ url: string }>('/subscriptions/create-checkout', { plan });
      window.location.href = data.url;
    } catch (error: any) {
      toast.error(error.message || 'Erro de conexão. Tente novamente.');
      setUpgradingPlan(null);
    }
  };

  const handleManageSubscription = async () => {
    setIsManaging(true);
    try {
      const data = await apiClient.post<{ url: string }>('/subscriptions/create-portal', {});
      window.location.href = data.url;
    } catch (error: any) {
      toast.error(error.message || 'Erro de conexão. Tente novamente.');
      setIsManaging(false);
    }
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
      toast.error('Apenas arquivos PNG e JPEG são permitidos');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('O arquivo deve ter no máximo 2MB');
      return;
    }
    setLogoFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setLogoPreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const handleSaveGeneral = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isOwner) { toast.error('Apenas o proprietário pode alterar as configurações'); return; }
    setIsSaving(true);
    try {
      await apiClient.put('/organizations/me', { name: companyName, timezone, language, dateFormat });
      if (logoFile) {
        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve((reader.result as string).split(',')[1]);
          reader.onerror = reject;
          reader.readAsDataURL(logoFile);
        });
        await apiClient.post('/organizations/me/logo', { image: base64, contentType: logoFile.type });
      }
      toast.success('Configurações atualizadas com sucesso!');
      await fetchData();
      await refreshOrg();
    } catch (error: any) {
      toast.error(error.message || 'Erro ao salvar configurações');
    } finally {
      setIsSaving(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) { toast.error('As senhas não coincidem'); return; }
    if (newPassword.length < 8) { toast.error('A nova senha deve ter no mínimo 8 caracteres'); return; }
    setIsChangingPassword(true);
    try {
      await updatePassword({ oldPassword: currentPassword, newPassword });
      toast.success('Senha alterada com sucesso!');
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
    } catch (err: any) {
      const msg = err.message || '';
      if (msg.includes('Incorrect') || msg.includes('incorrect')) toast.error('Senha atual incorreta');
      else if (msg.includes('policy') || msg.includes('password') && msg.includes('requirements')) toast.error('A nova senha não atende aos requisitos de segurança');
      else if (msg.includes('limit') || msg.includes('Limit') || msg.includes('attempt')) toast.error('Muitas tentativas. Aguarde alguns minutos.');
      else toast.error('Erro ao alterar senha. Tente novamente.');
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleExportData = async () => {
    if (!isOwner) { toast.error('Apenas o proprietário pode exportar dados'); return; }
    try {
      await apiClient.post('/organizations/me/export', {});
      toast.success('Exportação iniciada! Você receberá um email com o link para download.');
    } catch (error: any) {
      toast.error(error.message || 'Erro ao exportar dados');
    }
  };

  const handleDeleteOrganization = async () => {
    if (!isOwner) { toast.error('Apenas o proprietário pode deletar a organização'); return; }
    const confirmed = window.confirm('Tem certeza que deseja deletar a organização? Esta ação não pode ser desfeita.');
    if (!confirmed) return;
    try {
      await apiClient.delete('/organizations/me');
      toast.success('Organização marcada para exclusão.');
      await logout();
      navigate('/login');
    } catch (error: any) {
      toast.error(error.message || 'Erro ao deletar organização');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-muted-foreground">Carregando...</p>
      </div>
    );
  }

  const currentPlan = (subscription?.plan || 'free') as PlanType;
  const currentPlanIndex = PLAN_ORDER.indexOf(currentPlan);

  const usagePercentage = (current: number, max: number) => {
    if (max === -1) return 0;
    return Math.round((current / max) * 100);
  };

  const getUsageColor = (pct: number) => {
    if (pct >= 80) return 'text-red-600';
    if (pct >= 50) return 'text-yellow-600';
    return 'text-green-600';
  };

  const getBarColor = (pct: number) => {
    if (pct >= 80) return 'bg-red-600';
    if (pct >= 50) return 'bg-yellow-600';
    return 'bg-green-600';
  };

  return (
    <AppLayout
      actions={<Button variant="outline" onClick={() => navigate('/dashboard')}>Dashboard</Button>}
    >
      <div className="container mx-auto px-4 py-8 max-w-5xl">
        <div className="mb-8">
          <h2 className="text-3xl font-bold mb-2">Configurações da Organização</h2>
          <p className="text-muted-foreground">Gerencie as configurações e preferências da sua organização</p>
        </div>

        <Tabs defaultValue="general" className="space-y-6">
          <TabsList>
            <TabsTrigger value="general">Geral</TabsTrigger>
            <TabsTrigger value="account">Conta</TabsTrigger>
            <TabsTrigger value="subscription">Assinatura</TabsTrigger>
            <TabsTrigger value="data">Dados</TabsTrigger>
            <TabsTrigger value="career-page" onClick={() => navigate('/settings/career-page')}>Página de Carreiras</TabsTrigger>
          </TabsList>

          {/* General Tab */}
          <TabsContent value="general">
            <form onSubmit={handleSaveGeneral}>
              <Card>
                <CardHeader>
                  <CardTitle>Informações Gerais</CardTitle>
                  <CardDescription>Configure as informações básicas da sua organização</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <Label>Logo da Empresa</Label>
                    <div className="mt-2 flex items-center gap-4">
                      {logoPreview && <img src={logoPreview} alt="Logo" className="w-20 h-20 object-contain border rounded" />}
                      <Input type="file" accept="image/png,image/jpeg,image/jpg" onChange={handleLogoChange} disabled={!isOwner} />
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">PNG ou JPEG, máximo 2MB</p>
                  </div>
                  <div>
                    <Label htmlFor="companyName">Nome da Empresa *</Label>
                    <Input id="companyName" value={companyName} onChange={(e) => setCompanyName(e.target.value)} required disabled={!isOwner} />
                  </div>
                  <div>
                    <Label htmlFor="timezone">Fuso Horário</Label>
                    <select id="timezone" value={timezone} onChange={(e) => setTimezone(e.target.value)} disabled={!isOwner} className="w-full px-3 py-2 border rounded-md">
                      <option value="America/Sao_Paulo">Brasília (GMT-3)</option>
                      <option value="America/Manaus">Manaus (GMT-4)</option>
                      <option value="America/Rio_Branco">Rio Branco (GMT-5)</option>
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="language">Idioma</Label>
                    <select id="language" value={language} onChange={(e) => setLanguage(e.target.value)} disabled={!isOwner} className="w-full px-3 py-2 border rounded-md">
                      <option value="pt-BR">Português (Brasil)</option>
                      <option value="en-US">English (US)</option>
                      <option value="es-ES">Español</option>
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="dateFormat">Formato de Data</Label>
                    <select id="dateFormat" value={dateFormat} onChange={(e) => setDateFormat(e.target.value)} disabled={!isOwner} className="w-full px-3 py-2 border rounded-md">
                      <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                      <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                      <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                    </select>
                  </div>
                  {isOwner && <div className="pt-4"><Button type="submit" disabled={isSaving}>{isSaving ? 'Salvando...' : 'Salvar Alterações'}</Button></div>}
                  {!isOwner && <p className="text-sm text-muted-foreground">Apenas o proprietário pode alterar essas configurações</p>}
                </CardContent>
              </Card>
            </form>
          </TabsContent>

          {/* Account Tab */}
          <TabsContent value="account">
            <Card>
              <CardHeader>
                <CardTitle>Alterar Senha</CardTitle>
                <CardDescription>Atualize a senha da sua conta</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
                  <div className="space-y-2">
                    <Label htmlFor="currentPassword">Senha Atual</Label>
                    <Input id="currentPassword" type="password" placeholder="••••••••" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} required />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="newPassword">Nova Senha</Label>
                    <Input id="newPassword" type="password" placeholder="••••••••" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required />
                    <p className="text-xs text-muted-foreground">Mínimo 8 caracteres</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Confirmar Nova Senha</Label>
                    <Input id="confirmPassword" type="password" placeholder="••••••••" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required />
                  </div>
                  <Button type="submit" disabled={isChangingPassword}>{isChangingPassword ? 'Alterando...' : 'Alterar Senha'}</Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Subscription Tab */}
          <TabsContent value="subscription">
            <div className="space-y-6">
              {/* Usage Card */}
              <Card>
                <CardHeader>
                  <CardTitle>Uso de Recursos</CardTitle>
                  <CardDescription>
                    Plano atual: <span className="font-semibold capitalize">{currentPlan}</span>
                    {subscription?.status && (
                      <span className={`ml-2 px-2 py-0.5 rounded text-xs ${subscription.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                        {subscription.status}
                      </span>
                    )}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Jobs */}
                    <div>
                      <div className="flex justify-between mb-1">
                        <span className="text-sm">Vagas</span>
                        <span className={`text-sm font-medium ${getUsageColor(usagePercentage(subscription?.jobCount || 0, subscription?.maxJobs || 0))}`}>
                          {subscription?.jobCount} / {subscription?.maxJobs === -1 ? '∞' : subscription?.maxJobs}
                        </span>
                      </div>
                      {subscription?.maxJobs !== -1 && (
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className={`h-2 rounded-full ${getBarColor(usagePercentage(subscription?.jobCount || 0, subscription?.maxJobs || 0))}`}
                            style={{ width: `${Math.min(usagePercentage(subscription?.jobCount || 0, subscription?.maxJobs || 0), 100)}%` }} />
                        </div>
                      )}
                    </div>
                    {/* Candidates */}
                    <div>
                      <div className="flex justify-between mb-1">
                        <span className="text-sm">Candidatos (mês)</span>
                        <span className={`text-sm font-medium ${getUsageColor(usagePercentage(subscription?.candidateCount || 0, subscription?.maxCandidatesPerMonth || 0))}`}>
                          {subscription?.candidateCount} / {subscription?.maxCandidatesPerMonth === -1 ? '∞' : subscription?.maxCandidatesPerMonth}
                        </span>
                      </div>
                      {subscription?.maxCandidatesPerMonth !== -1 && (
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className={`h-2 rounded-full ${getBarColor(usagePercentage(subscription?.candidateCount || 0, subscription?.maxCandidatesPerMonth || 0))}`}
                            style={{ width: `${Math.min(usagePercentage(subscription?.candidateCount || 0, subscription?.maxCandidatesPerMonth || 0), 100)}%` }} />
                        </div>
                      )}
                    </div>
                    {/* Users */}
                    <div>
                      <div className="flex justify-between mb-1">
                        <span className="text-sm">Usuários</span>
                        <span className={`text-sm font-medium ${getUsageColor(usagePercentage(subscription?.userCount || 0, subscription?.maxUsers || 0))}`}>
                          {subscription?.userCount} / {subscription?.maxUsers === -1 ? '∞' : subscription?.maxUsers}
                        </span>
                      </div>
                      {subscription?.maxUsers !== -1 && (
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className={`h-2 rounded-full ${getBarColor(usagePercentage(subscription?.userCount || 0, subscription?.maxUsers || 0))}`}
                            style={{ width: `${Math.min(usagePercentage(subscription?.userCount || 0, subscription?.maxUsers || 0), 100)}%` }} />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Manage Subscription (for paid plans) */}
                  {currentPlan !== 'free' && isOwner && (
                    <div className="pt-4 mt-4 border-t">
                      <Button variant="outline" onClick={handleManageSubscription} disabled={isManaging}>
                        {isManaging ? 'Redirecionando...' : 'Gerenciar Assinatura'}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Plans Grid */}
              {isOwner && (
                <div>
                  <h3 className="text-lg font-semibold mb-4">Planos Disponíveis</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {PLAN_ORDER.map((planKey) => {
                      const plan = PLANS[planKey];
                      const planIndex = PLAN_ORDER.indexOf(planKey);
                      const isCurrent = planKey === currentPlan;
                      const isDowngrade = planIndex <= currentPlanIndex;
                      const isUpgrade = planIndex > currentPlanIndex;

                      return (
                        <div
                          key={planKey}
                          className={`relative rounded-lg border p-5 flex flex-col ${
                            plan.highlighted && !isCurrent ? 'border-blue-500 border-2 shadow-md' : ''
                          } ${isCurrent ? 'border-green-500 border-2 bg-green-50' : ''}`}
                        >
                          {plan.highlighted && !isCurrent && (
                            <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-500 text-white text-xs px-3 py-1 rounded-full">
                              Mais popular
                            </span>
                          )}
                          {isCurrent && (
                            <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-green-500 text-white text-xs px-3 py-1 rounded-full">
                              Plano atual
                            </span>
                          )}

                          <h4 className="text-lg font-bold">{plan.name}</h4>
                          <div className="mt-2">
                            <span className="text-2xl font-bold">{plan.price}</span>
                            {plan.priceDetail && <span className="text-sm text-muted-foreground">{plan.priceDetail}</span>}
                          </div>

                          <div className="mt-4 space-y-1 text-sm text-muted-foreground">
                            <p>📋 {plan.limits.jobs}</p>
                            <p>👤 {plan.limits.candidates}</p>
                            <p>👥 {plan.limits.users}</p>
                          </div>

                          <ul className="mt-4 space-y-1 text-sm flex-1">
                            {plan.features.map((f, i) => (
                              <li key={i} className="flex items-start gap-1">
                                <span className="text-green-500 mt-0.5">✓</span>
                                <span>{f}</span>
                              </li>
                            ))}
                          </ul>

                          <div className="mt-4">
                            {isCurrent ? (
                              <Button disabled className="w-full" variant="outline">Plano atual</Button>
                            ) : isUpgrade ? (
                              <Button
                                className="w-full"
                                variant={plan.highlighted ? 'default' : 'outline'}
                                onClick={() => handleUpgrade(planKey)}
                                disabled={upgradingPlan === planKey}
                              >
                                {upgradingPlan === planKey ? 'Redirecionando...' : plan.cta}
                              </Button>
                            ) : isDowngrade ? (
                              <Button disabled className="w-full" variant="ghost" size="sm">—</Button>
                            ) : null}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Data Tab */}
          <TabsContent value="data">
            <Card>
              <CardHeader>
                <CardTitle>Gerenciamento de Dados</CardTitle>
                <CardDescription>Exporte ou delete os dados da sua organização (LGPD)</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h3 className="font-semibold mb-2">Exportar Dados</h3>
                  <p className="text-sm text-muted-foreground mb-4">Exporte todos os dados em formato JSON.</p>
                  <Button variant="outline" onClick={handleExportData} disabled={!isOwner}>Exportar Dados</Button>
                </div>
                <div className="pt-6 border-t">
                  <h3 className="font-semibold mb-2 text-red-600">Zona de Perigo</h3>
                  <p className="text-sm text-muted-foreground mb-4">Deletar a organização removerá permanentemente todos os dados após 30 dias.</p>
                  <Button variant="destructive" onClick={handleDeleteOrganization} disabled={!isOwner}>Deletar Organização</Button>
                </div>
                {!isOwner && <p className="text-sm text-muted-foreground">Apenas o proprietário pode exportar ou deletar dados</p>}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AppLayout>
  );
}
